/**
 * The sheet element's step state, one object per owner. The element
 * creates each object once and hands it to its steps on the
 * `SheetElementPort`. Only the owning module writes an object's fields; every
 * other module reads them, or calls an operation the owner exports. The
 * census `sheet-element-state.deno.test.ts` holds that, and holds that no
 * port or host the element builds has a setter.
 *
 * | Object | Owner |
 * | --- | --- |
 * | `SheetBindState` | sheet-element-bind.ts |
 * | `SheetPresentationState` | sheet-element-presentation.ts |
 * | `ViewModelSwapState` | sheet-element-swap.ts |
 * | `CommittedRowGeometry` | sheet-bind-work.ts |
 */
import type { Option, RowNetworkKey } from "@tento-lona/sheets";
import type { StructuralCoHide } from "@tento-lona/sheets/platform";
import type { CellHeight } from "@tento-ui/components/sheet/cell-height";
import { BindGeneration } from "@tento-scroll/engine/scroll-engine";
import type { SheetPresentation } from "./sheet-presentation.ts";

/** The element's bind: its generation, settle controller and render promise. Owner: sheet-element-bind.ts. */
export class SheetBindState {
  /** The current bind generation (bumped on a switch to another sheet or renderer, and on a retry). */
  bindGeneration: BindGeneration = BindGeneration.ZERO;
  /** Aborts the current bind's settle work. */
  bindSettleController: AbortController = new AbortController();
  /** The presentation hold the current bind keeps until its render settles. */
  bindPresentationTicket: Option<SheetPresentation.Ticket> = null;
  /** `performance.now()` of the last bind's start (-Infinity before any bind). */
  lastBindStartedAtMs: number = Number.NEGATIVE_INFINITY;
  /** The last bind's render promise. */
  lastRenderPromise: Promise<void> = Promise.resolve();
  /** The id of `lastRenderPromise`, from `renderPromiseSeq`. */
  lastRenderPromiseId = 0;
  /** The render promise id sequence. */
  renderPromiseSeq = 0;
  /** Reveal chains waiting for the next render to be scheduled. */
  renderPromiseWaiters: (() => void)[] = [];
}

/**
 * The element's presentation: the navigation reveal and its overlay, and
 * what the reveal predicate reads. Owner: sheet-element-presentation.ts.
 */
export class SheetPresentationState {
  /** The presentation hold a navigation keeps. */
  navigationPresentationTicket: Option<SheetPresentation.Ticket> = null;
  /** Aborts the current navigation reveal. */
  navigationRevealController: Option<AbortController> = null;
  /** The navigation spinner, while it is shown. */
  navigationSpinnerEl: Option<HTMLElement> = null;
  /** Whether the navigation transition scrubbed the previous sheet. */
  navigationTransitionScrubbed = false;
  /** When the current presentation started loading. */
  presentationStartedAt = 0;
  /** The strip the reveal last judged. */
  presentationStrip: Option<CellHeight.VisibleStrip> = null;
  /** How the last settle was reached. */
  presentationVia: Option<string> = null;
  /** The reveal generation: a navigation state bumps it. */
  revealGeneration = 0;
  /**
   * The engine bind generation read at scrub time, the moment the reveal
   * contract's debt is incurred. The reveal chain passes it to
   * `whenSettled(minGeneration)`, so its fast path cannot release on the
   * transient empty-queue window a superseded switch produces.
   */
  scrubbedAtBindGeneration: Option<BindGeneration> = null;
  /**
   * The render promise id at scrub time. A cold navigation can reach "idle"
   * before its own render is scheduled; when the live id still equals this
   * snapshot at idle, the reveal chain waits for the next render.
   */
  scrubRenderPromiseId = 0;
}

/** The view-model swap bracket's depth and suppressed passes. Owner: sheet-element-swap.ts. */
export class ViewModelSwapState {
  /** The depth of in-flight view-model swaps. */
  viewModelSwapDepth = 0;
  /** Whole-sheet passes suppressed during the swap. */
  swapSuppressedPasses = 0;
}

/**
 * Row geometry as of the last committed bind: what the rendered columns now
 * paint. `rebindStructurePass` diffs the next pass against it (the co-hide).
 * Owner: sheet-bind-work.ts, which records it when a bind commits.
 */
export class CommittedRowGeometry {
  /** Each row's committed offset and cell height; `null` before the first commit. */
  committedRows: Option<Map<RowNetworkKey, StructuralCoHide.RowGeometry>> = null;
}
